import axios from 'axios';
import { useEffect, useState, useCallback, useMemo } from 'react';
import type {
  ListingAvailability,
  ListUserDetailsDTO,
  ParsedListing,
} from '@bb/domain/dist/types';
import type { ListTeamDTO } from '@bb/domain/dist/types/dto/ListingController/listTeam';

import errorReporter from '@app/errorReporter';
import { message } from 'components';
import { useDebounce } from 'hooks';
import { DASHBOARD_LISTING_STORAGE_KEYS, featureEnabled } from 'utils';
import { setWithExpiry, getWithExpiry } from 'utils/localStorageHelper';
import { addWeeks } from 'date-fns';

const ALL_AVAILABILITY_OPTIONS: ListingAvailability[] = [
  'Sold Conditionally',
  'Leased Conditionally',
  'Price Change',
  'Extended',
  'Deal Fell Through',
  'New',
  'Show For Backups',
  'Withdrawn',
  'Sold',
  'Leased',
  'Suspended',
  'Terminated',
  'Expired',
  'Combined listing',
  'Pending',
  'Temp Off Market',
];

const DEFAULT_AVAILABILITY_OPTIONS: Partial<ListingAvailability>[] = [
  'New',
  'Sold Conditionally',
  'Leased Conditionally',
  'Show For Backups',
  'Price Change',
  'Extended',
  'Deal Fell Through',
  'Pending',
];

type UseListingsProps = {
  searchType: 'user' | 'team';
  targetTeamId?: string;
  isSecondaryTeamCard?: boolean;
};

const useListings = ({
  searchType,
  targetTeamId,
  isSecondaryTeamCard,
}: UseListingsProps) => {
  const chronologicalShowingsEnabled = featureEnabled('chronologicalShowings');

  const [searchValue, setSearchValue] = useState('');
  const debouncedSearchValue = useDebounce(searchValue, 500);
  const [listings, setListings] = useState<ParsedListing[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState(
    getWithExpiry(DASHBOARD_LISTING_STORAGE_KEYS.status) || 'All',
  );
  const [availability, setAvailability] = useState<
    Partial<ListingAvailability>[]
  >(() => {
    const storedAvailabilityStr = getWithExpiry(
      DASHBOARD_LISTING_STORAGE_KEYS.availability,
    );
    // fixes case where when stored value is '', split returns ['']
    const storedAvailability = storedAvailabilityStr
      ? storedAvailabilityStr.split(', ')
      : null;
    return chronologicalShowingsEnabled && searchType === 'team'
      ? ALL_AVAILABILITY_OPTIONS
      : storedAvailability || DEFAULT_AVAILABILITY_OPTIONS;
  });
  const [availabilityOptions, setAvailabilityOptions] = useState<
    ListingAvailability[]
  >([]);

  useEffect(() => {
    setWithExpiry(
      DASHBOARD_LISTING_STORAGE_KEYS.status,
      status,
      addWeeks(new Date(), 1).getTime(), // the intention here is to clear the stored filter at the same time the user's session cookie expires
    );

    if (chronologicalShowingsEnabled) {
      // when chronologicalShowings is enabled, don't store team listing availability to preserve user listing's dropdown state
      // team listing availability is always "ALL_AVAILABILITY_OPTIONS" here
      // consider making team listing availability a separate storage key if divergent states are needed to be preserved
      if (searchType !== 'team') {
        setWithExpiry(
          DASHBOARD_LISTING_STORAGE_KEYS.availability,
          availability.join(', '),
          addWeeks(new Date(), 1).getTime(), // the intention here is to clear the stored filter at the same time the user's session cookie expires
        );
      }
    } else {
      setWithExpiry(
        DASHBOARD_LISTING_STORAGE_KEYS.availability,
        availability.join(', '),
        addWeeks(new Date(), 1).getTime(), // the intention here is to clear the stored filter at the same time the user's session cookie expires
      );
    }
  }, [status, availability, searchType]);

  useEffect(() => {
    setIsLoading(true);
    (async () => {
      try {
        const { data } = await axios.get<ListUserDetailsDTO | ListTeamDTO>(
          `/listing/${searchType}/0`,
          {
            params: {
              address: debouncedSearchValue,
              avail: 'All',
              location: 'All',
              locationAddress: 'All',
              searchType,
              status,
              limit: 10,
              sort_field: 'latestUpdateTime',
              targetTeamId,
              includeAccess: true,
            },
          },
        );

        setListings(data.listings);
      } catch (err) {
        errorReporter.error(err);
        message.error('Error loading listings');
      } finally {
        setIsLoading(false);
      }
    })();
  }, [searchType, status, debouncedSearchValue, targetTeamId]);

  useEffect(() => {
    if (!listings.length || isSecondaryTeamCard) return;
    const listingAvailabilities = new Set(
      listings.map(listing => listing.availability),
    );
    const filteredAvailabilityOptions = ALL_AVAILABILITY_OPTIONS.filter(
      option => listingAvailabilities.has(option),
    );

    // Update availability options only if the list is initially empty
    // or if the new filtered options are more than the previous options
    if (
      !availabilityOptions.length ||
      filteredAvailabilityOptions.length > availabilityOptions.length
    )
      setAvailabilityOptions(filteredAvailabilityOptions);

    if (chronologicalShowingsEnabled) {
      if (
        searchType === 'team' &&
        availability.length === ALL_AVAILABILITY_OPTIONS.length
      ) {
        setAvailability(filteredAvailabilityOptions);
      }
    } else {
      setAvailability(prevAvailability =>
        prevAvailability.filter(option =>
          filteredAvailabilityOptions.includes(option),
        ),
      );
    }
  }, [availabilityOptions, isSecondaryTeamCard, listings]);

  const onStatusChange = useCallback((newStatus: string) => {
    setStatus(newStatus);
  }, []);

  const onAvailabilityChange = useCallback(
    (newAvailability: ListingAvailability[]) => {
      setAvailability(newAvailability);
    },
    [],
  );

  const filteredListingsByAvailability = useMemo(
    () => {
      if (!chronologicalShowingsEnabled) {
        return listings.filter(item =>
          availability.includes(item.availability),
        );
      }

      if (
        searchType === 'team' &&
        availabilityOptions.length === 0 &&
        availability.length === ALL_AVAILABILITY_OPTIONS.length
      ) {
        return listings;
      }

      const allOptionsSelected =
        availabilityOptions.length > 0 &&
        availability.length === availabilityOptions.length;
      return allOptionsSelected
        ? listings
        : listings.filter(item => availability.includes(item.availability));
    },
    !chronologicalShowingsEnabled
      ? [availability, listings]
      : [availability, listings, availabilityOptions, searchType],
  );

  return {
    availability,
    availabilityOptions,
    filteredListingsByAvailability,
    listings,
    onStatusChange,
    onAvailabilityChange,
    isLoading,
    searchValue,
    setSearchValue,
  };
};

export default useListings;
